import { useCallback, useRef } from "react";
import { RequestHelper } from "../../../utils/requestHelper";
import { _ROUTES } from "../../../utils/_routes_constants";
import { _ACTIONS, _MESSAGES } from "../../../utils/constants";
import { createToast, createToastOnLastItemSaved } from "../../../utils/toast";
import { buildIRI } from "../../../utils/function"; // Fonction pour générer les URLs API

/**
 * Hook pour gérer les mises à jour des cellules Ag-Grid avec PATCH/POST
 * @param {Object} params - Contient site, fiscalYear, et version
 * @param {Function} setPendingRefresh - Fonction pour rafraîchir les données après modification
 * @returns {Function} Callback pour gérer les changements de cellule
 */
export function useSalesOnCellValueChanged(params, setPendingRefresh) {
  const requestHelper = new RequestHelper();
  const pendingUpdatesRef = useRef(0);

  return useCallback(
    async (agParams) => {
      const { colDef, data, newValue } = agParams;
      const { site, fiscalYear, version } = params;
      const fiscalMonth = parseInt(colDef.field);
      const id = data.inputs[fiscalMonth];

      if (!site || !fiscalYear || !version) {
        createToast(
          "warning",
          "Données manquantes ou invalides. Vérifiez votre saisie.",
        );
        return;
      }

      const payload = {
        site: buildIRI("/sites", site),
        fiscalMonth,
        fiscalYear,
        version: version || 1,
        ...(data.subThemes === "External Cash" && {
          externalCashVolume: parseInt(newValue),
        }),
        ...(data.subThemes === "External Credit" && {
          externalCreditVolume: parseInt(newValue),
        }),
        ...(data.subThemes === "Internal" && {
          internalVolume: parseInt(newValue),
        }),
      };

      const action = id ? _ACTIONS.PATCH : _ACTIONS.POST;
      const url = id
        ? `${_ROUTES.IPT_SALE_BLOCK_MONTH}/${id}`
        : _ROUTES.IPT_SALE_BLOCK_MONTH;

      pendingUpdatesRef.current++;

      requestHelper
        .doRequest(action, url, payload)
        .catch(() => {
          createToast("error", _MESSAGES.error);
        })
        .finally(() => {
          pendingUpdatesRef.current--;

          if (pendingUpdatesRef.current === 0) {
            setPendingRefresh((prev) => !prev);

            createToastOnLastItemSaved(pendingUpdatesRef);
          }
        });
    },
    [params, setPendingRefresh],
  );
}
